<template>
  <div>
    <b-modal
      modal-class="component-modal-payment-method"
      id="PaymentMethodModal"
      ref="PaymentMethodModal"
      title="ชำระด้วย"
      hide-footer
    >
      <b-form @submit.prevent>
        <!-- DentCloud Payment Group - Always visible -->
        <div class="mb-2 p-2 dentcloud-section">
          <h4 class="text-primary mb-3">DentCloud Payment</h4>
          <b-card
            bg-variant="light"
            class="dentcloud-card mb-0"
            body-class="p-2"
            :class="{ 'dentcloud-disabled': dentCloudPayments.length === 0 }"
          >
            <div v-if="dentCloudPayments.length > 0">
              <div v-for="method in dentCloudPayments" :key="method.id">
                <b-form-group class="mb-2" label-cols="4">
                  <template #label>
                    <b-form-checkbox
                      v-model="selectedDentCloudMethod"
                      :value="method.id"
                      switch
                      :disabled="
                        nhsoBookingAvailable ||
                        (selectedDentCloudMethod &&
                          selectedDentCloudMethod !== method.id)
                      "
                      @change="handleDentCloudCheckbox($event, method)"
                    >
                      <span v-b-tooltip="getTooltipText(method)">
                        {{ method.name }}
                        <i
                          v-if="method.processingFee"
                          class="fas fa-info-circle text-muted"
                        ></i
                        >:
                      </span>
                    </b-form-checkbox>
                  </template>
                  <b-row>
                    <b-input-group class="col" append="บาท">
                      <b-form-input
                        lazy
                        class="text-right"
                        type="number"
                        v-model="method.amount"
                        :disabled="selectedDentCloudMethod !== method.id"
                        @change="checkValue(method)"
                      ></b-form-input>
                    </b-input-group>
                  </b-row>
                </b-form-group>
              </div>
            </div>
            <div v-else class="text-muted">
              สมัครใช้ DentCloud Payment ง่ายๆ เพียงติดต่อ
              <a href="https://lin.ee/jJ1H6Tn" target="_blank">Admin</a> วันนี้!
            </div>
          </b-card>
        </div>
        <hr class="mt-0" />

        <!-- Deposit Payment Section -->
        <div v-if="hasDepositMethods" class="mb-2 p-2 deposit-section">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h4 class="text-success mb-0">
              <i class="fas fa-piggy-bank mr-1"></i>
              เงินมัดจำ
            </h4>
            <span class="text-success font-weight-bold">
              คงเหลือ {{ formatCurrency(depositBalance) }} บาท
            </span>
          </div>
          <b-card v-if="depositBalance > 0" bg-variant="light" class="mb-0" body-class="p-2">
            <div v-for="method in depositPaymentMethods" :key="method.id">
              <b-form-group class="mb-2" label-cols="4">
                <template #label>
                  <b-form-checkbox
                    v-model="selectedDepositMethod"
                    :value="method.id"
                    switch
                    :disabled="nhsoBookingAvailable || depositBalance <= 0"
                    @change="handleDepositCheckbox($event, method)"
                  >
                    {{ method.name }}:
                  </b-form-checkbox>
                </template>
                <b-row>
                  <b-input-group class="col" append="บาท">
                    <b-form-input
                      lazy
                      class="text-right"
                      type="number"
                      v-model="depositPaymentAmount"
                      :max="depositBalance"
                      :disabled="selectedDepositMethod !== method.id"
                      @change="checkDepositValue"
                    ></b-form-input>
                  </b-input-group>
                </b-row>
              </b-form-group>
            </div>
            <small class="text-muted">
              <i class="fas fa-info-circle"></i>
              ยอดเงินมัดจำจะถูกหักอัตโนมัติเมื่อออกใบเสร็จ
            </small>
          </b-card>
        </div>
        <hr v-if="hasDepositMethods" class="mt-0" />

        <!-- Other Payment Methods -->
        <div
          v-for="(methodCat, index) in filteredPaymentMethods"
          :key="methodCat.id"
        >
          <div v-if="methodCat.mReceiptPayment.length > 1">
            <b-button
              class="mb-2"
              size="sm"
              block
              :variant="getVariant(methodCat.id)"
              v-b-toggle="'my-payment-method-' + index"
              >{{ methodCat.name }}</b-button
            >
            <b-collapse :id="'my-payment-method-' + index">
              <div v-for="method in methodCat.mReceiptPayment" :key="method.id">
                <b-form-group class="mb-2" label-cols="4">
                  <template #label>
                    <b-form-checkbox
                      v-model="method.checked"
                      switch
                      @change="handleOtherCheckbox($event, method)"
                      :disabled="
                        [1, 2, 3, 4, 5, 6].includes(methodCat.id) &&
                        nhsoBookingAvailable
                      "
                    >
                      <span v-b-tooltip="getTooltipText(method)">
                        {{ method.name }}
                        <i
                          v-if="method.processingFee"
                          class="fas fa-info-circle text-muted"
                        ></i
                        >:
                      </span>
                    </b-form-checkbox>
                  </template>
                  <b-row>
                    <b-input-group class="col" append="บาท">
                      <b-form-input
                        lazy
                        class="text-right"
                        type="number"
                        v-model="method.amount"
                        :disabled="!method.checked"
                        @change="checkValue(method)"
                      ></b-form-input>
                    </b-input-group>
                  </b-row>
                </b-form-group>
              </div>
            </b-collapse>
            <hr class="my-2" />
          </div>
          <div v-else>
            <div v-for="method in methodCat.mReceiptPayment" :key="method.id">
              <b-form-group class="mb-0" label-cols="4">
                <template #label>
                  <b-form-checkbox
                    v-model="method.checked"
                    switch
                    @change="handleOtherCheckbox($event, method)"
                    :disabled="
                      (methodCat.id === 6 && !linkedWithKtbPaotang) ||
                      (methodCat.id === 7 && !nhsoBookingAvailable) ||
                      ([1, 2, 3, 4, 5, 6].includes(methodCat.id) &&
                        nhsoBookingAvailable)
                    "
                  >
                    <span>
                      <img
                        v-if="methodCat.id === 6"
                        class="mr-1"
                        style="width: auto; height: 24px"
                        src="@/assets/img/Paotang-small.png"
                      />
                      <span v-b-tooltip="getTooltipText(method)">
                        {{ method.name }}
                        <i
                          v-if="method.processingFee"
                          class="fas fa-info-circle text-muted"
                        ></i
                        >:
                      </span>
                    </span>
                  </b-form-checkbox>
                </template>
                <b-row>
                  <b-input-group class="col" append="บาท">
                    <b-form-input
                      lazy
                      class="text-right"
                      type="number"
                      v-model="method.amount"
                      :disabled="!method.checked"
                      @change="checkValue(method)"
                    ></b-form-input>
                  </b-input-group>
                </b-row>
              </b-form-group>
              <b-alert
                class="mt-2"
                v-if="methodCat.id === 7 && !nhsoBookingAvailable"
                variant="warning"
                show
              >
                ไม่สามารถชำระด้วยวิธีนี้ได้ เนื่องจากไม่ใช่คนไข้ที่เบิกจ่าย
                "โครงการ30 บาทรักษาทุกที่"
              </b-alert>
              <b-alert
                class="mt-2"
                v-else-if="methodCat.id === 7"
                variant="success"
                show
              >
                ชำระด้วยวิธีนี้ได้วิธีเดียว เนื่องจากเป็นคนไข้ที่เบิกจ่ายตาม
                "โครงการ30 บาทรักษาทุกที่"
              </b-alert>
              <hr class="my-2" />
            </div>
          </div>
        </div>

        <b-form-group
          label-cols="4"
          label-align="right"
          label="รวม:"
          :state="totalState"
        >
          <b-input-group append="บาท">
            <b-form-input
              :value="totalPayment"
              :state="totalState"
              number
              readonly
            ></b-form-input>
          </b-input-group>
        </b-form-group>
        <b-form-group label-cols="4" label-align="right" label="ยอดชำระวันนี้:">
          <b-input-group append="บาท">
            <b-form-input class :value="receipt.paid" readonly></b-form-input>
          </b-input-group>
        </b-form-group>

        <b-button
          class="float-right"
          type="button"
          @click="previewReceipt"
          variant="primary"
          >พิมพ์ใบเสร็จ</b-button
        >
      </b-form>
      <PaymentReceiptPreviewModal
        ref="paymentReceiptPreviewModal"
        @afterPrint="closeAllModal"
      ></PaymentReceiptPreviewModal>
      <DcPaymentWebsocket />

      <Loading v-if="isLoading"></Loading>
      <Dialog ref="Dialog"></Dialog>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { required } from "vuelidate/lib/validators";
import Loading from "@/components/Loading.vue";
import Dialog from "@/components/modal/Dialog.vue";
import PaymentReceiptPreviewModal from "@/components/modal/PaymentReceiptPreviewModal.vue";
import DcPaymentWebsocket from "@/components/function/DcPaymentWebsocket.vue";

export default {
  name: "PaymentMethod",
  components: {
    Loading,
    Dialog,
    PaymentReceiptPreviewModal,
    DcPaymentWebsocket,
  },
  props: ["appointmentLinkedWithKtbPaotang"],
  data() {
    return {
      isLoading: false,
      paymentMethods: [],
      receipt: {},
      patient: {},
      apptId: null,
      totalPayment: 0,
      AccFee: 0,
      calItem: [],
      methodVariant: {
        1: "secondary",
        2: "danger",
        3: "info",
        4: "success",
        5: "warning",
      },
      collapseState: {},
      selectedDentCloudMethod: null,
      selectedDepositMethod: null,
      depositPaymentAmount: 0,
    };
  },
  created() {
    this.init();
  },
  mounted() {},
  watch: {
    paymentMethods: {
      deep: true,
      handler() {
        this.calTotalPayment();
      },
    },
    selectedDepositMethod() {
      this.calTotalPayment();
    },
    depositPaymentAmount() {
      this.calTotalPayment();
    },
  },
  computed: {
    ...mapGetters({
      paymentMethodList: "moduleUser/getPaymentMethodList",
      paymentMethodCategories: "moduleUser/getPaymentMethodCategories",
      getNhsoBookingNo: "moduleAppointment/getNhsoBookingNo",
      depositBalance: "moduleDeposit/getDepositBalance",
    }),

    totalState() {
      return this.round2(this.receipt.paid) === this.round2(this.totalPayment);
    },
    linkedWithKtbPaotang() {
      return (
        this.patient.linkedWithKtbPaotang &&
        this.appointmentLinkedWithKtbPaotang
      );
    },
    nhsoBookingAvailable() {
      return !!this.getNhsoBookingNo;
    },
    dentCloudPayments() {
      return this.paymentMethods.flatMap(category =>
        category.mReceiptPayment.filter(method => method.dcPaymentType)
      );
    },

    filteredPaymentMethods() {
      return this.paymentMethods
        .filter(category => category.type !== "Deposit")
        .map(category => ({
          ...category,
          mReceiptPayment: category.mReceiptPayment.filter(
            method => !method.dcPaymentType
          ),
        }))
        .filter(category => category.mReceiptPayment.length > 0);
    },
    depositPaymentMethods() {
      const category = this.paymentMethods.find(cat => cat.type === "Deposit");
      return category?.mReceiptPayment || [];
    },
    hasDepositMethods() {
      return this.depositPaymentMethods.length > 0;
    },
  },
  methods: {
    ...mapActions({
      fetchPaymentMethodList: "moduleUser/fetchPaymentMethodList",
      fetchDepositSummary: "moduleDeposit/fetchDepositSummary",
    }),
    init() {},
    show({ receipt, patient, appointment }) {
      this.setDefaultValue();
      this.$bvModal.show("PaymentMethodModal");
      this.receipt = Object.assign({}, receipt);
      this.uneditedItem = [...receipt.receiptItems];
      this.patient = Object.assign({}, patient);
      this.apptId = appointment.id;
      this.isLoading = true;

      const processCategories = (categories) => {
        this.paymentMethods = categories.map(methodCat => ({
          ...methodCat,
          mReceiptPayment: methodCat.mReceiptPayment
            .filter(method => !method.del)
            .map(method => ({
              ...method,
              processingFee: method.processingFee || null,
            })),
        }));
        this.initializeDentCloudPayments();
      };

      const finalize = () => {
        this.calAccFee();
        this.calProcessingFee();

        if (this.nhsoBookingAvailable) {
          this.$nextTick(() => {
            this.autoProcessNhsoPayment();
          });
        }
        this.isLoading = false;
      };

      // Use cached data from store if available, otherwise fetch
      if (this.paymentMethodCategories && this.paymentMethodCategories.length > 0) {
        processCategories(this.paymentMethodCategories);
        finalize();
      } else {
        this.fetchPaymentMethodList({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
        })
          .then(res => {
            processCategories(res.data.result);
          })
          .finally(() => {
            finalize();
          });
      }
    },
    initializeDentCloudPayments() {
      this.dentCloudPayments.forEach(method => {
        this.$set(method, "amount", null);
      });
      this.selectedDentCloudMethod = null;
    },
    autoProcessNhsoPayment() {
      if (this.nhsoBookingAvailable) {
        // Find NHSO payment method (category with id 7)
        const nhsoCategory = this.paymentMethods.find(
          category => category.id === 7
        );

        if (nhsoCategory && nhsoCategory.mReceiptPayment.length > 0) {
          const nhsoMethod = nhsoCategory.mReceiptPayment[0];

          // Check the payment method
          this.$set(nhsoMethod, "checked", true);

          // Set the amount to match the full payment required
          this.$set(nhsoMethod, "amount", this.receipt.paid);

          // Calculate everything
          this.calAccFee();
          this.calProcessingFee();

          // If the total matches, preview the receipt automatically
          this.$nextTick(() => {
            if (this.totalState) {
              this.previewReceipt();
            }
          });
        }
      }
    },
    previewReceipt() {
      if (this.totalPayment != this.receipt.paid) {
        this.$refs.Dialog.showAlertInfo(
          `กรุณาชำระให้ครบ ${this.receipt.paid} บาท`,
          "",
          "warning"
        );
        return;
      }
      this.sumPaymentMethod();
      this.receipt.receiptItems = [...this.calItem];
      this.$refs.paymentReceiptPreviewModal.show(this.receipt, this.patient);
    },
    sumPaymentMethod() {
      let paymentMethodAmount = [],
        cateAmount = [];
      this.paymentMethods.forEach(method => {
        let sum = 0;
        method.mReceiptPayment.forEach(i => {
          if (i.amount) {
            paymentMethodAmount.push({
              receiptPaymentId: i.id,
              amount: i.amount,
              dcPaymentType: i.dcPaymentType,
            });

            if (i.settlementType === "receivable") {
              cateAmount.push({
                cateId: method.id,
                cateName: `${method.name} (${i.name})`,
                cateNameEn: `${method.nameEn || method.name} (${i.nameEn || i.name})`,
                amount: i.amount,
              });
            } else {
              sum = sum + i.amount;
            }
          }
        });
        if (sum) {
          cateAmount.push({
            cateId: method.id,
            cateName: method.name,
            cateNameEn: method.nameEn,
            amount: sum,
          });
        }
      });

      // Include deposit payment if used
      if (this.selectedDepositMethod && this.depositPaymentAmount > 0) {
        const depositMethod = this.depositPaymentMethods.find(
          m => m.id === this.selectedDepositMethod
        );
        const depositCategory = this.paymentMethods.find(
          cat => cat.type === "Deposit"
        );

        if (depositMethod && depositCategory) {
          paymentMethodAmount.push({
            receiptPaymentId: depositMethod.id,
            amount: this.depositPaymentAmount,
            type: "Deposit",
          });
          cateAmount.push({
            cateId: depositCategory.id,
            cateName: depositCategory.name,
            cateNameEn: depositCategory.nameEn,
            amount: this.depositPaymentAmount,
            type: "Deposit",
          });
        }
      }

      this.receipt.receiptPayment = paymentMethodAmount;
      this.receipt.displayAmount = cateAmount;
    },
    closeAllModal() {
      this.$bvModal.hide("PaymentMethodModal");
      this.$bvModal.hide("PaymentModal");
    },
    handleDentCloudCheckbox(checked, method) {
      if (checked) {
        this.selectedDentCloudMethod = method.id;
        this.recalculate(method);
        // Uncheck other DentCloud payment methods
        this.dentCloudPayments.forEach(m => {
          if (m.id !== method.id) {
            this.$set(m, "amount", null);
          }
        });
      } else {
        this.selectedDentCloudMethod = null;
        this.$set(method, "amount", null);
      }
      this.$nextTick(() => {
        this.calAccFee();
        this.calProcessingFee();
      });
    },
    handleOtherCheckbox(checked, method) {
      if (checked) {
        this.recalculate(method);
      } else {
        this.$set(method, "amount", null);
      }
      this.$nextTick(() => {
        this.calAccFee();
        this.calProcessingFee();
      });
    },
    handleDepositCheckbox(checked, method) {
      if (checked) {
        this.selectedDepositMethod = method.id;
        // Calculate remaining amount needed
        this.$nextTick(() => {
          const remain = this.round2(this.receipt.paid - this.totalPayment);
          // Use the minimum of remaining amount and deposit balance
          this.depositPaymentAmount = Math.min(
            Math.max(remain, 0),
            this.depositBalance
          );
        });
      } else {
        this.selectedDepositMethod = null;
        this.depositPaymentAmount = 0;
      }
    },
    checkDepositValue() {
      // Ensure deposit amount is within valid range
      let amount = parseFloat(this.depositPaymentAmount) || 0;
      amount = Math.max(0, Math.min(amount, this.depositBalance));
      this.depositPaymentAmount = amount;
    },
    formatCurrency(value) {
      return this.$utils.formatCurrency(value);
    },
    recalculate(method) {
      this.$set(method, "amount", 0);
      this.$nextTick(() => {
        let remain = this.round2(this.receipt.paid - this.totalPayment);
        if (method?.id === 2) {
          remain = this.setMinMax(remain, 0, 900); //สำหรับกรณีประกันสังคม Set config ???
        }
        this.$set(method, "amount", remain < 0 ? 0 : remain);
      });
    },

    setMinMax(i, min, max) {
      let num = Math.min(Math.max(i, min), max) || 0;
      return +parseFloat(num).toFixed(2);
    },
    checkValue(method) {
      let calAmount = Math.max(method.amount, 0);
      this.$nextTick(() => {
        this.$set(method, "amount", calAmount);
        this.calAccFee();
        this.calProcessingFee();
      });
    },
    calTotalPayment() {
      let sum = 0;
      this.paymentMethods.forEach(method =>
        method.mReceiptPayment.forEach(i => {
          sum += i.amount ? +parseFloat(i.amount).toFixed(2) : 0;
        })
      );
      // Include deposit payment amount
      if (this.selectedDepositMethod && this.depositPaymentAmount > 0) {
        sum += +parseFloat(this.depositPaymentAmount).toFixed(2);
      }
      this.totalPayment = this.round2(sum);
    },
    round2(value) {
      return +(parseFloat(value) || 0).toFixed(2);
    },

    calAccFee() {
      // Get all checked payment methods (both DentCloud and others)
      let checkedMethods = [];

      // Check DentCloud payments
      if (this.selectedDentCloudMethod) {
        const selectedDentCloud = this.dentCloudPayments.find(
          method => method.id === this.selectedDentCloudMethod
        );
        if (selectedDentCloud?.amount) {
          checkedMethods.push(selectedDentCloud);
        }
      }

      // Check other payment methods
      this.paymentMethods.forEach(category => {
        category.mReceiptPayment.forEach(method => {
          if (method.checked && method.amount && !method.dcPaymentType) {
            checkedMethods.push(method);
          }
        });
      });

      // Calculate total processing fee
      this.AccFee = checkedMethods.reduce((prev, cur) => {
        const processingFee = cur.processingFee || 0;
        const amount = parseFloat(cur.amount) || 0;
        const paid = parseFloat(this.receipt.paid) || 1; // Prevent division by zero

        return prev + processingFee * (amount / paid);
      }, 0);

    },
    calProcessingFee() {
      this.calItem = this.uneditedItem.map(i => {
        return {
          ...i,
          processingFeeBaht: 0,
        };
      });
    },
    roundNum(num) {
      return Math.round((num + Number.EPSILON) * 100) / 100;
    },
    getVariant(id) {
      return this.methodVariant[id];
    },
    getTooltipText(method) {
      return method.processingFee
        ? `Processing fee: ${method.processingFee}%`
        : "";
    },

    setDefaultValue() {
      this.paymentMethods = [];
      this.receipt = {};
      this.patient = {};
      this.apptId = null;
      this.totalPayment = 0;
      this.AccFee = 0;
      this.selectedDentCloudMethod = null;
      this.selectedDepositMethod = null;
      this.depositPaymentAmount = 0;
    },
  },
};
</script>

<style>
</style>
